import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Row, Col,
} from 'reactstrap';

import { requestGuestLinkRequest } from '@reducers/guest/GuestAction';
import LocaleService from '@services/LocaleService';
import { CustomButton } from '@shared/buttons';
import { Error404Icon } from '@shared/icons';

import '../GuestReferralStyle.scss';

export type LinkState =
| 'link_invalid'
| 'link_expired'
| 'link_superseded'
| 'link_revoked'
| 'recipient_has_account'
// ENH-052: a clinic's public referral link that the clinic switched off.
| 'link_inactive';

export const LINK_STATES: LinkState[] = [
  'link_invalid', 'link_expired', 'link_superseded', 'link_revoked', 'recipient_has_account',
];

type LinkStateScreenProps = {
  state: LinkState;
  token?: string;
  // The page's own wording for the state (ENH-052's public referral link).
  copy?: { title: string; message: string };
};

const i18n = LocaleService.getTranslations('referrals.guest') as any;
const buttonI18n = LocaleService.getTranslations('general.button') as any;

/**
 * ENH-052 — the screens for a link that cannot show the referral: invalid,
 * expired, superseded by a newer email, revoked by the sender, or belonging
 * to someone who has since made an account.
 *
 * Deliberately reuses the existing error-page pattern (`.error-container`,
 * `.title`, `.message`, a single primary `CustomButton`) from
 * components/error/404/PageNotFound so a guest sees the same visual language as
 * every other full-page state in the app.
 */
const LinkStateScreen = ({
  state: initialState, token, copy: copyOverride,
}: LinkStateScreenProps) => {
  const dispatch = useDispatch<any>();
  const [ state, setState ] = useState<LinkState>(initialState);
  const [ requesting, setRequesting ] = useState(false);
  const [ requested, setRequested ] = useState(false);
  const [ failed, setFailed ] = useState(false);

  const copy = copyOverride ?? i18n.linkStates[state] ?? i18n.linkStates.link_invalid;

  // A fresh link is offered only for a link that simply ran out (expired) or
  // was replaced by a newer email (superseded). An invalid token identifies
  // nobody; a revoked one was turned off by the sender, and only they can undo
  // that; someone with an account signs in instead.
  const canRequestNewLink = !!token && (state === 'link_expired' || state === 'link_superseded');

  const onRequestNewLink = async () => {
    setRequesting(true);
    setFailed(false);

    try {
      await dispatch(requestGuestLinkRequest(token as string)).$promise;
      setRequested(true);
    } catch (error: any) {
      // The link's state can change between opening it and asking (the
      // sender revokes it, the recipient registers): show that state rather
      // than a generic "try again".
      const code = error?.response?.data?.error_code;
      if (LINK_STATES.includes(code) && code !== state) {
        setState(code);
      } else {
        setFailed(true);
      }
    } finally {
      setRequesting(false);
    }
  };

  const renderAction = () => {
    if (canRequestNewLink) {
      return (
        <CustomButton
          id="guest-request-new-link"
          className="btn-lg go-to-home-btn"
          color="primary"
          loading={requesting}
          disabled={requesting}
          onClick={onRequestNewLink}
        >
          {i18n.linkStates.requestNewLink}
        </CustomButton>
      );
    }

    if (state === 'recipient_has_account') {
      return (
        <CustomButton
          id="guest-go-to-login"
          className="btn-lg go-to-home-btn"
          color="primary"
          onClick={() => { window.location.href = '/auth/login'; }}
        >
          {buttonI18n.go_to_login}
        </CustomButton>
      );
    }

    return (
      <CustomButton
        id="guest-create-account"
        className="btn-lg go-to-home-btn"
        color="primary"
        onClick={() => { window.location.href = '/auth/register'; }}
      >
        {i18n.createAccount}
      </CustomButton>
    );
  };

  return (
    <div className="error-container guest-link-state">
      <Row className="mb-5">
        <Col>
          <Error404Icon />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <div className="title">{requested ? i18n.linkStates.sent.title : copy.title}</div>
        </Col>
      </Row>
      <Row className="mb-5">
        <Col>
          <div className="message">
            {requested ? i18n.linkStates.sent.message : copy.message}
          </div>
          {failed && (
            <div className="message guest-link-state-error mt-2">
              {i18n.linkStates.requestFailed}
            </div>
          )}
        </Col>
      </Row>
      {/* A switched-off clinic link (ENH-052) has no action: */}
      {/* its message says how to reach the clinic. */}
      {!requested && state !== 'link_inactive' && (
        <Row>
          <Col>
            {renderAction()}
          </Col>
        </Row>
      )}
    </div>
  );
};

export default LinkStateScreen;
